import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import gsap from 'gsap';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Plus,
  Minus,
  Truck,
  Shield,
  RotateCcw,
  Headphones,
  ChevronDown,
  ChevronUp,
  Mic,
  Sparkles,
  BatteryCharging,
  Smartphone,
  Star,
} from 'lucide-react';
import { FadeUp } from '../components/AnimatedSection';
import { useLanguage } from '../context/LanguageContext';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../components/ui/accordion';
import SmartSummariesSection from '../components/SmartSummariesSection';
import GALLERY_FRONT from '../assets/scribai-gallery-front.jpg';
import GALLERY_BACK from '../assets/scribai-gallery-back.jpg';
import GALLERY_INHAND from '../assets/scribai-gallery-inhand.jpg';
import GALLERY_DETAIL from '../assets/scribai-gallery-detail.jpg';

const appleEase = [0.16, 1, 0.3, 1];

// Subtle cursor-following 3D tilt, desktop-only by nature (needs mousemove — touch devices
// simply never fire it, so mobile just shows the static card). gsap.quickTo gives a springy,
// interruptible follow without spinning up a new tween on every mousemove event.
const TiltCard = ({ children, className = '' }) => {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    gsap.set(el, { transformPerspective: 800 });
    const rotateX = gsap.quickTo(el, 'rotationX', { duration: 0.5, ease: 'power3.out' });
    const rotateY = gsap.quickTo(el, 'rotationY', { duration: 0.5, ease: 'power3.out' });

    const handleMove = (e) => {
      const rect = el.getBoundingClientRect();
      const px = (e.clientX - rect.left) / rect.width - 0.5;
      const py = (e.clientY - rect.top) / rect.height - 0.5;
      rotateY(px * 8);
      rotateX(-py * 8);
    };
    const handleLeave = () => {
      rotateX(0);
      rotateY(0);
    };

    el.addEventListener('mousemove', handleMove);
    el.addEventListener('mouseleave', handleLeave);
    return () => {
      el.removeEventListener('mousemove', handleMove);
      el.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};

const colorVariants = [
  {
    id: 'navy',
    name: { en: 'Navy', es: 'Azul Marino' },
    hex: '#1c2e4a',
    shopifyVariantId: '56851013534028',
    image: 'https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/2zfnn3kc_ScribAI%20Navy.jpg',
  },
  {
    id: 'midnight-blue',
    name: { en: 'Midnight Blue', es: 'Azul Medianoche' },
    hex: '#1a1a3a',
    shopifyVariantId: '56851013468492',
    image: 'https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/iaq7d4je_ScribAI%20Midnight%20Blue.jpg',
  },
  {
    id: 'slate',
    name: { en: 'Slate', es: 'Pizarra' },
    hex: '#3e4e5e',
    shopifyVariantId: '56851013435724',
    image: 'https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/3xxawxcm_ScribAI%20Slate.jpg',
  },
  {
    id: 'titanium',
    name: { en: 'Titanium', es: 'Titanio' },
    hex: '#8B8589',
    shopifyVariantId: '56851013501260',
    image: 'https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/8w8qjkle_ScribAI%20Titanium.jpg',
  },
  {
    id: 'graphite',
    name: { en: 'Graphite', es: 'Grafito' },
    hex: '#383838',
    shopifyVariantId: '56851013566796',
    image: 'https://customer-assets.emergentagent.com/job_voice-to-insights-2/artifacts/vdiwimol_ScribAI%20Graphite.jpg',
  },
];

const sharedImages = [GALLERY_FRONT, GALLERY_BACK, GALLERY_INHAND, GALLERY_DETAIL];

const PRICES = {
  es: { price: 89.99, original: 119.99, symbol: '€', currency: 'EUR' },
  en: { price: 97.99, original: 159, symbol: '$', currency: 'USD' },
};
const PAYMENT_CDN = 'https://cdn.jsdelivr.net/gh/activemerchant/payment_icons/app/assets/images/payment_icons';

const paymentMethods = [
  { id: 'visa', alt: 'Visa' },
  { id: 'american_express', alt: 'American Express' },
  { id: 'apple_pay', alt: 'Apple Pay' },
  { id: 'diners_club', alt: 'Diners Club' },
  { id: 'discover', alt: 'Discover' },
  { id: 'google_pay', alt: 'Google Pay' },
  { id: 'master', alt: 'Mastercard' },
  { id: 'paypal', alt: 'PayPal' },
  { id: 'shopify_pay', alt: 'Shop Pay' },
  { id: 'klarna', alt: 'Klarna' },
];

const PaymentIcons = () => (
  <div className="flex flex-wrap justify-center gap-2">
    {paymentMethods.map(({ id, alt }) => (
      <div key={id} className="rounded-md overflow-hidden bg-white flex items-center justify-center" style={{ width: 50, height: 32 }}>
        <img
          src={`${PAYMENT_CDN}/${id}.svg`}
          alt={alt}
          className="w-full h-full object-contain"
          loading="lazy"
        />
      </div>
    ))}
  </div>
);

const ProductPage = () => {
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showWhatsInBox, setShowWhatsInBox] = useState(false);
  const [pastHero, setPastHero] = useState(false);
  const [nearFooter, setNearFooter] = useState(false);
  const heroSentinelRef = useRef(null);
  const footerSentinelRef = useRef(null);
  const { language } = useLanguage();

  const galleryImages = sharedImages;

  const handleColorChange = (index) => {
    setSelectedColor(index);
    setSelectedImageIndex(0);
  };

  useEffect(() => {
    const sentinel = heroSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setPastHero(!entry.isIntersecting),
      { rootMargin: '0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Hides the sticky bar once the page's own buy CTA (in the reviews/FAQ
  // area near the bottom) is back in view, so it doesn't permanently
  // overlap the footer below it.
  useEffect(() => {
    const sentinel = footerSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setNearFooter(entry.isIntersecting),
      { rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const showStickyBar = pastHero && !nearFooter;

  const texts = {
    en: {
      heroEyebrow: 'AI Voice Recorder',
      tagline: 'Record, transcribe and summarize any conversation automatically.',
      coreFeatures: 'Core Features',
      includesFree: 'Includes',
      freeTranscription: '400 minutes/month FREE AI transcription (lifetime)',
      transcriptionDesc: 'AI transcription in 81 languages with >98% accuracy. Generate summaries, mind maps, and action items powered by GPT-5 technology.',
      vipUpgrade: 'VIP Upgrade',
      vipDesc: 'Additional 400 minutes/month for 1 year. Minutes reset monthly and do not roll over.',
      ultraSlim: 'Ultra-slim design',
      ultraSlimDesc: 'Only 3.3mm thin and 27g. 30 hours of recording, magnetic fast charging.',
      smartRecording: 'Smart dual 6D microphone',
      smartRecordingDesc: 'Automatically switches between phone call and in-person modes.',
      color: 'Color',
      quantity: 'Quantity',
      agreeTerms: 'I agree to the',
      termsConditions: 'Terms and Conditions',
      buyNow: 'Buy Now',
      mustAgree: 'Please agree to the terms and conditions to proceed',
      secureCheckout: 'Secure Checkout',
      whatsInBox: "What's in the box",
      recorder: 'ScribAI Recorder',
      cable: 'USB-C Charging Cable',
      case: 'Premium Carrying Case',
      guide: 'Quick Start Guide',
      subscription: '1-Year ScribAI Premium',
      warrantyCard: 'Warranty Card',
      freeShipping: 'Free Shipping',
      freeShippingDesc: 'Free shipping on all orders.',
      warranty: '2-Year Warranty',
      warrantyDesc: 'Warranty period subject to local laws.',
      returns: 'Free 30-Day Returns',
      returnsDesc: 'No reason needed. Free return shipping & full refund.',
      support: 'Lifetime Customer Support',
      supportDesc: 'Quick support within 24 hours on working days.',
      eyebrowFeatures: 'Features',
      featuresHeading: 'Why ScribAI',
      featuresSubheading: 'Everything you need to capture, understand, and act on your conversations.',
      eyebrowSpecs: 'Specifications',
      specsHeading: 'Built with precision',
      eyebrowFaq: 'FAQ',
      faqHeading: 'Common questions',
      feat1Title: 'AI Transcription',
      feat1Desc: '81 languages supported with over 98% accuracy. Speaker identification and timestamps included.',
      feat2Title: 'Smart Summaries',
      feat2Desc: 'GPT-5 powered summarization creates mind maps, action items, and key insights automatically.',
      feat3Title: '30 Hours Recording',
      feat3Desc: 'Record all day with 30 hours of battery life. Magnetic fast charging gets you back to 100% quickly.',
      feat4Title: 'Ultra Slim Design',
      feat4Desc: 'Only 3.3mm thin and weighing just 27g. Fits in any pocket or attaches magnetically.',
      faq1Q: 'How does the transcription work?',
      faq1A: 'Simply sync your recordings to the ScribAI app. Our AI processes your audio and delivers accurate transcripts within minutes.',
      faq2Q: 'What happens after the free 400 minutes?',
      faq2A: 'You can upgrade to VIP for additional minutes, or wait until your monthly allowance resets. Minutes do not roll over.',
      faq3Q: 'Is my data secure?',
      faq3A: 'Yes. All recordings are encrypted during transfer and storage. You have full control over your data.',
      faq4Q: 'What languages are supported?',
      faq4A: 'ScribAI supports 81 languages for transcription with over 98% accuracy.',
      specModel: 'Model',
      specAudioFormat: 'Audio Format',
      specSampling: 'Sampling',
      specStorage: 'Storage',
      specBattery: 'Battery',
      specWeight: 'Weight',
      specDimensions: 'Dimensions',
      specThickness: 'Thickness',
      specCharging: 'Charging',
      specBatteryVal: '30 Hours',
      specChargingVal: 'Magnetic USB-C',
      eyebrowReviews: 'Reviews',
      reviewsHeading: 'Loved by early users',
      review1Author: 'Maria S.',
      review1Text: 'I record every client call now. The transcripts are shockingly accurate, even with background noise.',
      review2Author: 'James T.',
      review2Text: "Finally a recorder that doesn't need babysitting. It just syncs and the summary is ready.",
      review3Author: 'Elena R.',
      review3Text: 'The AI summaries save me an hour a day going through lecture notes. Wish I had this in college.',
    },
    es: {
      heroEyebrow: 'Grabadora de Voz IA',
      tagline: 'Graba, transcribe y resume cualquier conversación automáticamente.',
      coreFeatures: 'Caracteristicas Principales',
      includesFree: 'Incluye',
      freeTranscription: '400 minutos/mes GRATIS de transcripcion IA (de por vida)',
      transcriptionDesc: 'Transcripcion IA en 81 idiomas con >98% de precision. Genera resumenes, mapas mentales y tareas con tecnologia GPT-5.',
      vipUpgrade: 'Mejora VIP',
      vipDesc: '400 minutos adicionales/mes por 1 ano. Los minutos se reinician mensualmente y no se acumulan.',
      ultraSlim: 'Diseno ultra delgado',
      ultraSlimDesc: 'Solo 3.3mm de grosor y 27g. 30 horas de grabacion, carga magnetica rapida.',
      smartRecording: 'Microfono dual 6D inteligente',
      smartRecordingDesc: 'Cambia automaticamente entre llamadas y conversaciones presenciales.',
      color: 'Color',
      quantity: 'Cantidad',
      agreeTerms: 'Acepto los',
      termsConditions: 'Terminos y Condiciones',
      buyNow: 'Comprar Ahora',
      mustAgree: 'Por favor acepta los terminos y condiciones para continuar',
      secureCheckout: 'Pago Seguro',
      whatsInBox: 'Que incluye',
      recorder: 'Grabadora ScribAI',
      cable: 'Cable de carga USB-C',
      case: 'Estuche Premium',
      guide: 'Guia de inicio rapido',
      subscription: '1 Ano de ScribAI Premium',
      warrantyCard: 'Tarjeta de garantia',
      freeShipping: 'Envio Gratis',
      freeShippingDesc: 'Envio gratis en todos los pedidos.',
      warranty: 'Garantia de 2 Anos',
      warrantyDesc: 'Periodo de garantia sujeto a leyes locales.',
      returns: 'Devolucion Gratis 30 Dias',
      returnsDesc: 'Sin motivo necesario. Envio de devolucion gratis y reembolso total.',
      support: 'Soporte de Por Vida',
      supportDesc: 'Soporte rapido en 24 horas en dias laborables.',
      eyebrowFeatures: 'Caracteristicas',
      featuresHeading: 'Por que ScribAI',
      featuresSubheading: 'Todo lo que necesitas para capturar, entender y actuar en tus conversaciones.',
      eyebrowSpecs: 'Especificaciones',
      specsHeading: 'Construido con precision',
      eyebrowFaq: 'Preguntas',
      faqHeading: 'Preguntas frecuentes',
      feat1Title: 'Transcripcion IA',
      feat1Desc: 'Soporta 81 idiomas con mas del 98% de precision. Incluye identificacion de hablantes y marcas de tiempo.',
      feat2Title: 'Resumenes Inteligentes',
      feat2Desc: 'Resumenes con GPT-5 que crean mapas mentales, tareas y puntos clave automaticamente.',
      feat3Title: '30 Horas de Grabacion',
      feat3Desc: 'Graba todo el dia con 30 horas de bateria. La carga magnetica rapida te devuelve al 100% rapidamente.',
      feat4Title: 'Diseno Ultra Delgado',
      feat4Desc: 'Solo 3.3mm de grosor y 27g de peso. Cabe en cualquier bolsillo o se adhiere magneticamente.',
      faq1Q: 'Como funciona la transcripcion?',
      faq1A: 'Simplemente sincroniza tus grabaciones con la app ScribAI. Nuestra IA procesa tu audio y entrega transcripciones precisas en minutos.',
      faq2Q: 'Que pasa despues de los 400 minutos gratis?',
      faq2A: 'Puedes mejorar a VIP para minutos adicionales, o esperar hasta que se reinicie tu cuota mensual. Los minutos no se acumulan.',
      faq3Q: 'Mis datos estan seguros?',
      faq3A: 'Si. Todas las grabaciones estan encriptadas durante la transferencia y almacenamiento. Tienes control total sobre tus datos.',
      faq4Q: 'Que idiomas se soportan?',
      faq4A: 'ScribAI soporta 81 idiomas para transcripcion con mas del 98% de precision.',
      specModel: 'Modelo',
      specAudioFormat: 'Formato de Audio',
      specSampling: 'Muestreo',
      specStorage: 'Almacenamiento',
      specBattery: 'Bateria',
      specWeight: 'Peso',
      specDimensions: 'Dimensiones',
      specThickness: 'Grosor',
      specCharging: 'Carga',
      specBatteryVal: '30 Horas',
      specChargingVal: 'Magnetica USB-C',
      eyebrowReviews: 'Opiniones',
      reviewsHeading: 'Lo que dicen los primeros usuarios',
      review1Author: 'Maria S.',
      review1Text: 'Ahora grabo todas mis llamadas con clientes. Las transcripciones son sorprendentemente precisas, incluso con ruido de fondo.',
      review2Author: 'James T.',
      review2Text: 'Por fin una grabadora que no hay que estar vigilando. Se sincroniza sola y el resumen ya esta listo.',
      review3Author: 'Elena R.',
      review3Text: 'Los resumenes con IA me ahorran una hora al dia repasando apuntes de clase. Ojala lo hubiera tenido en la universidad.',
    }
  };

  const txt = texts[language] || texts.es;
  const { price: PRICE, original: ORIGINAL_PRICE, symbol, currency } = PRICES[language] || PRICES.es;
  const discountPercent = Math.round(((ORIGINAL_PRICE - PRICE) / ORIGINAL_PRICE) * 100);

  useEffect(() => {
    if (window.fbq) {
      fbq('track', 'ViewContent', {
        content_name: 'ScribAI',
        content_type: 'product',
        value: PRICE,
        currency: currency
      });
    }
  }, [PRICE, currency]);

  const handleBuyNow = () => {
    if (!agreedToTerms) {
      alert(txt.mustAgree);
      return;
    }
    const variantId = colorVariants[selectedColor].shopifyVariantId;
    if (window.fbq) {
      fbq('track', 'InitiateCheckout', {
        value: PRICE * quantity,
        currency: currency
      });
    }
    window.location.replace(`https://shop.tivorao.com/cart/${variantId}:${quantity}`);
  };

  // +1 for the Smart Summaries slide, which renders the live animated component
  // instead of a photo — see the main-image area below.
  const totalSlides = galleryImages.length + 1;

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % totalSlides);
  };

  const prevImage = () => {
    setSelectedImageIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const inTheBox = [
    txt.recorder, txt.cable, txt.case, txt.guide, txt.subscription, txt.warrantyCard,
  ];

  const features = [
    { icon: Mic, title: txt.feat1Title, desc: txt.feat1Desc },
    { icon: Sparkles, title: txt.feat2Title, desc: txt.feat2Desc },
    { icon: BatteryCharging, title: txt.feat3Title, desc: txt.feat3Desc },
    { icon: Smartphone, title: txt.feat4Title, desc: txt.feat4Desc },
  ];

  const faqs = [
    [txt.faq1Q, txt.faq1A],
    [txt.faq2Q, txt.faq2A],
    [txt.faq3Q, txt.faq3A],
    [txt.faq4Q, txt.faq4A],
  ];

  const reviews = [
    { author: txt.review1Author, text: txt.review1Text, rating: 5 },
    { author: txt.review2Author, text: txt.review2Text, rating: 5 },
    { author: txt.review3Author, text: txt.review3Text, rating: 4 },
  ];

  const specs = [
    [txt.specModel, 'ScribAI'],
    [txt.specAudioFormat, 'MP3 / WAV'],
    [txt.specSampling, '16-bit / 48kHz'],
    [txt.specStorage, '64GB Built-in'],
    [txt.specBattery, txt.specBatteryVal],
    [txt.specWeight, '27g (0.96 oz)'],
    [txt.specDimensions, '3.35 x 2.13 x 0.13 in'],
    [txt.specThickness, '3.3mm'],
    [txt.specCharging, txt.specChargingVal],
  ];

  const trustItems = [
    { icon: Truck, title: txt.freeShipping, desc: txt.freeShippingDesc },
    { icon: Shield, title: txt.warranty, desc: txt.warrantyDesc },
    { icon: RotateCcw, title: txt.returns, desc: txt.returnsDesc },
    { icon: Headphones, title: txt.support, desc: txt.supportDesc },
  ];

  return (
    <div data-testid="product-page" className="min-h-screen bg-[var(--bg-page)]">
      {/* Hero / Main Product Section */}
      <section className="relative overflow-hidden pt-24 md:pt-28 pb-16 px-4">
        {/* Ambient background glow, scoped to the hero so it never interacts with scroll compositing */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full bg-[var(--accent-link)]/10 blur-[120px]" />
          <div className="absolute bottom-[-30%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-[var(--accent-link)]/[0.06] blur-[120px]" />
        </div>
        <div className="container-wide">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Left - Image Gallery */}
            <div className="flex gap-4">
              {/* Thumbnails */}
              <div className="hidden md:flex flex-col gap-2 w-20 overflow-y-auto max-h-[500px] hide-scrollbar">
                {galleryImages.map((img, index) => (
                  <button
                    key={`${selectedColor}-${index}`}
                    onClick={() => setSelectedImageIndex(index)}
                    data-testid={`thumbnail-${index}`}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 ${
                      selectedImageIndex === index
                        ? 'border-[var(--accent-link)]'
                        : 'border-transparent opacity-50 hover:opacity-80'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
                <button
                  onClick={() => setSelectedImageIndex(galleryImages.length)}
                  data-testid={`thumbnail-${galleryImages.length}`}
                  className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all flex-shrink-0 flex items-center justify-center bg-[var(--bg-elevated)] ${
                    selectedImageIndex === galleryImages.length
                      ? 'border-[var(--accent-link)]'
                      : 'border-transparent opacity-50 hover:opacity-80'
                  }`}
                >
                  <Sparkles size={22} className="text-[var(--accent-link)]" />
                </button>
              </div>

              {/* Main Image */}
              <div className="flex-1 relative glass-card rounded-2xl overflow-hidden">
                <div
                  className="absolute inset-0 pointer-events-none transition-all duration-500"
                  style={{
                    background: `radial-gradient(ellipse 60% 60% at 50% 50%, ${colorVariants[selectedColor].hex}33 0%, transparent 70%)`
                  }}
                />
                <div
                  className={
                    selectedImageIndex === galleryImages.length
                      ? 'flex items-center justify-center p-6 relative z-10'
                      : 'aspect-square flex items-center justify-center p-4 relative z-10'
                  }
                >
                  <AnimatePresence mode="wait">
                    {selectedImageIndex === galleryImages.length ? (
                      <motion.div
                        key={`${selectedColor}-smart-summaries`}
                        data-testid="main-product-image"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{ duration: 0.4, ease: appleEase }}
                        className="w-full"
                      >
                        <SmartSummariesSection />
                      </motion.div>
                    ) : (
                      <motion.img
                        key={`${selectedColor}-${selectedImageIndex}`}
                        src={galleryImages[selectedImageIndex]}
                        alt="ScribAI Product"
                        data-testid="main-product-image"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.97 }}
                        transition={{ duration: 0.4, ease: appleEase }}
                        className="max-w-full max-h-full object-contain rounded-lg"
                      />
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={prevImage}
                  data-testid="prev-image-btn"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[var(--bg-page)]/60 hover:bg-[var(--bg-page)]/80 flex items-center justify-center transition-colors z-20"
                >
                  <ChevronLeft size={20} className="text-white" />
                </button>
                <button
                  onClick={nextImage}
                  data-testid="next-image-btn"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[var(--bg-page)]/60 hover:bg-[var(--bg-page)]/80 flex items-center justify-center transition-colors z-20"
                >
                  <ChevronRight size={20} className="text-white" />
                </button>

                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-[var(--bg-page)]/60 px-3 py-1 rounded-full text-xs text-white z-20">
                  {selectedImageIndex + 1} / {totalSlides}
                </div>
              </div>
            </div>

            {/* Right - Product Info */}
            <div className="space-y-6">
              {/* Eyebrow + Title + Price */}
              <div>
                <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] text-[var(--accent-link)] mb-3">
                  {txt.heroEyebrow}
                </span>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
                  ScribAI Recorder
                </h1>
                <p className="text-[var(--text-secondary)] mb-4">{txt.tagline}</p>
                <div className="flex items-center gap-3">
                  <p className="text-3xl font-semibold text-[var(--text-primary)]">{symbol}{PRICE.toFixed(2)}</p>
                  <p className="text-xl text-[var(--text-secondary)] line-through">{symbol}{ORIGINAL_PRICE.toFixed(2)}</p>
                  <span className="text-sm font-medium text-[var(--accent-success)] bg-[var(--accent-success)]/10 px-2 py-1 rounded-full">-{discountPercent}%</span>
                </div>
              </div>

              {/* Color Selector */}
              <div data-testid="color-selector-section">
                <p className="text-sm text-[var(--text-secondary)] mb-3">
                  {txt.color}: <span className="text-[var(--text-primary)]">{colorVariants[selectedColor].name[language] || colorVariants[selectedColor].name.es}</span>
                </p>
                <div className="flex gap-3">
                  {colorVariants.map((variant, index) => (
                    <button
                      key={variant.id}
                      onClick={() => handleColorChange(index)}
                      data-testid={`color-${variant.id}`}
                      className={`w-9 h-9 rounded-full transition-all duration-200 ${
                        selectedColor === index
                          ? 'ring-2 ring-[var(--accent-link)] ring-offset-2 ring-offset-[var(--bg-page)] scale-110'
                          : 'hover:scale-105 opacity-70 hover:opacity-100'
                      }`}
                      style={{ backgroundColor: variant.hex }}
                      title={variant.name[language] || variant.name.es}
                    />
                  ))}
                </div>
              </div>

              {/* Core Features */}
              <div className="space-y-4 glass-card rounded-2xl p-5">
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">{txt.coreFeatures}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex gap-3">
                    <span className="text-[var(--accent-success)] font-medium">{txt.includesFree}:</span>
                    <span className="text-[var(--text-primary)]">{txt.freeTranscription}</span>
                  </div>
                  <p className="text-[var(--text-secondary)]">{txt.transcriptionDesc}</p>
                  <div className="flex gap-3 pt-2">
                    <span className="text-[var(--accent-gold)] font-medium">{txt.vipUpgrade}:</span>
                    <span className="text-[var(--text-secondary)]">{txt.vipDesc}</span>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <span className="text-[var(--text-primary)] font-medium">{txt.ultraSlim}:</span>
                    <span className="text-[var(--text-secondary)]">{txt.ultraSlimDesc}</span>
                  </div>
                  <div className="flex gap-3">
                    <span className="text-[var(--text-primary)] font-medium">{txt.smartRecording}:</span>
                    <span className="text-[var(--text-secondary)]">{txt.smartRecordingDesc}</span>
                  </div>
                </div>
              </div>

              {/* Quantity Selector */}
              <div>
                <p className="text-sm text-[var(--text-secondary)] mb-3">{txt.quantity}</p>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    data-testid="quantity-decrease"
                    className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <Minus size={16} className="text-white" />
                  </button>
                  <span className="text-xl font-medium text-[var(--text-primary)] w-8 text-center" data-testid="quantity-value">
                    {quantity}
                  </span>
                  <button
                    onClick={() => setQuantity(quantity + 1)}
                    data-testid="quantity-increase"
                    className="w-10 h-10 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
                  >
                    <Plus size={16} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Terms Checkbox */}
              <div className="flex items-start gap-3">
                <button
                  onClick={() => setAgreedToTerms(!agreedToTerms)}
                  data-testid="terms-checkbox"
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors ${
                    agreedToTerms
                      ? 'bg-[var(--accent-link)] border-[var(--accent-link)]'
                      : 'border-[#424245] hover:border-[var(--text-secondary)]'
                  }`}
                >
                  {agreedToTerms && <Check size={14} className="text-white" />}
                </button>
                <label className="text-sm text-[var(--text-secondary)]">
                  {txt.agreeTerms}{' '}
                  <a href="/terms" className="text-[var(--accent-link)] hover:underline">
                    {txt.termsConditions}
                  </a>
                </label>
              </div>

              {/* Buy Button */}
              <motion.button
                onClick={handleBuyNow}
                data-testid="buy-now-button"
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className="w-full py-4 rounded-xl font-semibold text-lg transition-colors bg-[var(--accent-link)] text-white hover:bg-[var(--accent-link-hover)] shadow-[0_0_30px_-8px_var(--accent-glow)]"
              >
                {txt.buyNow} — {symbol}{(PRICE * quantity).toFixed(2)}
              </motion.button>

              {/* Payment Icons */}
              <div className="text-center">
                <p className="text-xs text-[var(--text-secondary)] mb-4">{txt.secureCheckout}</p>
                <PaymentIcons />
              </div>

              {/* What's in the Box */}
              <div className="border-t border-[#1E2A4A] pt-4">
                <button
                  onClick={() => setShowWhatsInBox(!showWhatsInBox)}
                  data-testid="whats-in-box-toggle"
                  className="w-full flex items-center justify-between py-2"
                >
                  <span className="font-medium text-[var(--text-primary)]">{txt.whatsInBox}</span>
                  {showWhatsInBox ? <ChevronUp size={20} className="text-[var(--text-secondary)]" /> : <ChevronDown size={20} className="text-[var(--text-secondary)]" />}
                </button>
                <AnimatePresence>
                  {showWhatsInBox && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="py-4 space-y-2">
                        {inTheBox.map((item, index) => (
                          <div key={index} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                            <Check size={14} className="text-[var(--accent-success)]" />
                            <span>{item}</span>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Shipping & Info Icons */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#1E2A4A]">
                {trustItems.map(({ icon: Icon, title, desc }, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Icon size={20} className="text-[var(--accent-link)] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-[var(--text-primary)]">{title}</p>
                      <p className="text-xs text-[var(--text-secondary)]">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Sentinel marking the end of the hero, used to trigger the sticky mobile bar */}
        <div ref={heroSentinelRef} />
      </section>

      {/* Numbered Feature Cards */}
      <section className="relative border-t border-[#1E2A4A] py-20 md:py-28 px-4">
        <div className="container-wide">
          <FadeUp className="mb-12 md:mb-16 max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--accent-link)]">
              {txt.eyebrowFeatures}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text-primary)] mt-3 mb-3 tracking-tight">
              {txt.featuresHeading}
            </h2>
            <p className="text-[var(--text-secondary)]">{txt.featuresSubheading}</p>
          </FadeUp>

          <div className="grid sm:grid-cols-2 gap-6">
            {features.map(({ icon: Icon, title, desc }, index) => (
              <FadeUp key={index} delay={index * 0.08}>
                <TiltCard>
                  <div className="group relative overflow-hidden glass-card rounded-2xl p-8 h-full transition-colors hover:border-[var(--accent-link)]/40">
                    <div
                      className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-20 group-hover:opacity-30 transition-opacity pointer-events-none"
                      style={{ background: 'var(--accent-link)' }}
                    />
                    <div className="relative flex items-center justify-between mb-6">
                      <div className="w-12 h-12 rounded-xl bg-[var(--accent-link)]/10 flex items-center justify-center">
                        <Icon size={22} className="text-[var(--accent-link)]" />
                      </div>
                      <span className="text-xs font-mono text-[var(--text-tertiary)]">0{index + 1}</span>
                    </div>
                    <h3 className="relative text-lg font-semibold text-[var(--text-primary)] mb-2">{title}</h3>
                    <p className="relative text-sm text-[var(--text-secondary)] leading-relaxed">{desc}</p>
                  </div>
                </TiltCard>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Specifications - glassmorphism */}
      <section className="border-t border-[#1E2A4A] py-20 md:py-28 px-4">
        <div className="container-wide">
          <FadeUp className="mb-12 md:mb-16 max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--accent-link)]">
              {txt.eyebrowSpecs}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text-primary)] mt-3 tracking-tight">
              {txt.specsHeading}
            </h2>
          </FadeUp>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8 items-start">
            <FadeUp delay={0.05}>
              <div className="glass-card rounded-2xl p-8 md:p-10">
                {specs.map(([label, value], index) => (
                  <div
                    key={index}
                    className={`flex justify-between py-3 ${index !== specs.length - 1 ? 'border-b border-[#1E2A4A]' : ''}`}
                  >
                    <span className="text-[var(--text-secondary)]">{label}</span>
                    <span className="text-[var(--text-primary)] font-medium">{value}</span>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp delay={0.15}>
              <div className="glass-card rounded-2xl p-8 md:p-10">
                <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-5">{txt.whatsInBox}</h3>
                <div className="space-y-3">
                  {inTheBox.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 text-sm text-[var(--text-secondary)]">
                      <Check size={16} className="text-[var(--accent-success)] flex-shrink-0" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="border-t border-[#1E2A4A] py-20 md:py-28 px-4">
        <div className="container-wide max-w-3xl">
          <FadeUp className="mb-10 md:mb-12">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--accent-link)]">
              {txt.eyebrowFaq}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text-primary)] mt-3 tracking-tight">
              {txt.faqHeading}
            </h2>
          </FadeUp>

          <FadeUp delay={0.1}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map(([q, a], index) => (
                <AccordionItem value={`item-${index}`} key={index} className="border-[#1E2A4A]">
                  <AccordionTrigger className="text-[var(--text-primary)] hover:no-underline hover:text-[var(--accent-link)] text-base md:text-lg py-5">
                    {q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--text-secondary)] text-sm md:text-base leading-relaxed">
                    {a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </FadeUp>
        </div>
      </section>

      {/* Reviews / Social Proof */}
      <section className="border-t border-[#1E2A4A] py-20 md:py-28 px-4">
        <div className="container-wide">
          <FadeUp className="mb-12 md:mb-16 max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[var(--accent-link)]">
              {txt.eyebrowReviews}
            </span>
            <h2 className="text-3xl md:text-4xl font-semibold text-[var(--text-primary)] mt-3 tracking-tight">
              {txt.reviewsHeading}
            </h2>
          </FadeUp>

          <div className="grid sm:grid-cols-3 gap-6">
            {reviews.map((review, index) => (
              <FadeUp key={index} delay={index * 0.08}>
                <div className="glass-card rounded-2xl p-6 h-full">
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={14} className="fill-[var(--accent-gold)] text-[var(--accent-gold)]" />
                    ))}
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] leading-relaxed mb-4">"{review.text}"</p>
                  <p className="text-sm font-medium text-[var(--text-primary)]">{review.author}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* Sentinel marking the approach of the footer, used to hide the sticky mobile bar */}
      <div ref={footerSentinelRef} />

      {/* Sticky mobile buy bar */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.3, ease: appleEase }}
            className="fixed bottom-0 inset-x-0 z-40 lg:hidden bg-[var(--bg-surface)]/95 backdrop-blur-xl border-t border-[#1E2A4A] px-4 py-3 flex items-center justify-between gap-4"
          >
            <div>
              <p className="text-xs text-[var(--text-secondary)]">ScribAI Recorder</p>
              <p className="text-lg font-semibold text-[var(--text-primary)]">{symbol}{(PRICE * quantity).toFixed(2)}</p>
            </div>
            <button
              onClick={handleBuyNow}
              data-testid="sticky-buy-now-button"
              className="px-6 py-3 rounded-xl font-semibold bg-[var(--accent-link)] text-white hover:bg-[var(--accent-link-hover)] transition-colors"
            >
              {txt.buyNow}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProductPage;
